import {reactive, readonly, toRaw} from "vue";
import cleanAssign from "@/clean-assign";
import {BackendFetch, Host, Processes} from "@hantera/portal-app";
import {type AccessControlEntry, type PersonalAccessToken, type User} from "@hantera/portal-contracts";
import {WebSocketMessage} from "@/portal-websocket-client";

// The implementation of ACL below is a simplified version on the C# version. It does not check for attributes as
// those are not available in the client.

interface AccessControlListQuery {
  resource: string[]
  permission: string[]
}

class AccessControlList {
  public static readonly FullAccess = '*';

  private readonly root: ResourceNode;

  constructor(entries?: AccessControlEntry[]) {
    this.root = new ResourceNode();

    if (entries) {
      entries.forEach(entry => this.insert(entry));
    }
  }

  private insert(entry: AccessControlEntry): void {
    if (!entry.permission) {
      // If permission is null, no action is taken.
      return;
    }

    let currentNode = this.root;

    entry.resource.forEach(part => {
      if (!currentNode.children[part]) {
        currentNode.children[part] = new ResourceNode();
      }
      currentNode = currentNode.children[part];

      if (currentNode.hasFullAccess) {
        return; // No need to continue if full access is already granted by a parent node.
      }
    });

    if (entry.permission.includes(AccessControlList.FullAccess)) {
      currentNode.hasFullAccess = true;
    }
    currentNode.permissions.add(new PermissionNode(entry.permission));
  }

  public contains(query: AccessControlListQuery): boolean {
    let currentNode = this.root;
    let matchNode = new ResourceNode();

    for (let i = 0; i < query.resource.length; i++) {
      const pathPath = query.resource[i];

      if (currentNode.hasFullAccess) {
        return true; // Full access to current node and children.
      }

      if (currentNode.children[pathPath]) {
        currentNode = currentNode.children[pathPath];
        if (currentNode.permissions.size > 0) {
          currentNode.permissions.forEach(permission => matchNode.permissions.add(permission));
        }
      } else {
        break;
      }

      if (i === query.resource.length - 1 && query.permission.length === 0 && Object.keys(currentNode.children).length > 0) {
        return true;
      }
    }

    if (query.permission.length === 0 && matchNode.permissions.size > 0) {
      return true;
    }

    return matchNode.hasAccess(query);
  }
}

class ResourceNode {
  public children: Record<string, ResourceNode> = {};
  public hasFullAccess: boolean = false;
  public permissions: Set<PermissionNode> = new Set<PermissionNode>();

  public hasAccess(query: AccessControlListQuery): boolean {
    if (this.permissions.size === 0) return false;

    for (const permission of this.permissions) {

      let hasAccess = true;
      for (let i = 0; i < query.permission.length; i++) {
        if (permission.permission[i] !== AccessControlList.FullAccess && permission.permission[i] !== query.permission[i]) {
          hasAccess = false;
          break;
        }
      }
      if (hasAccess) return true;
    }

    return false;
  }
}

class PermissionNode {
  constructor(public permission: Readonly<string[]>) {}

  public equals(other: PermissionNode): boolean {
    return this.permission.length === other.permission.length &&
        this.permission.every((p, i) => p === other.permission[i]);
  }
}


const user = reactive<User>({
  identityId: '',
  name: '',
  email: '',
  phone: '',
  picture: undefined,
  acl: [],
  settings: {}
});

let acl: AccessControlList = new AccessControlList();

export default function (
  portalFetch: BackendFetch,
  host: Host,
  processes: Processes,
  webSocketSubscribe: (handler: (message: WebSocketMessage) => void) => void
) {

  webSocketSubscribe(message => {
    if (message.type === 'userSettingsUpdated') {
      for (const key in message.values) {
        user.settings[key] = message.values[key];
      }
    }
  })
  
  return {
    value: readonly(user),

    async refresh() {
      const res = await portalFetch('/user', {
        method: 'GET'
      });

      if (res.status === 200) {
        const userData = await res.json();
        cleanAssign(user, userData);

        // Parse pendingEmailExpiresAt as Date
        if (userData.pendingEmailExpiresAt) {
          user.pendingEmailExpiresAt = new Date(userData.pendingEmailExpiresAt);
        }
      } else if (res.status === 401) {
        cleanAssign(user, {});
      }

      acl = new AccessControlList(user.acl);

      host.send({
        type: 'user',
        user: toRaw(user)
      });
    },

    async updateProfile(data: { name?: string; phone?: string; picture?: string }) {
      const res = await portalFetch('/user/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!res.ok) {
        processes.error({
          title: 'Could not update profile',
          message: `Failed to update profile. Server responded (${res.status}): ${await res.text()}`
        });
        throw new Error('Profile update failed');
      }

      const updatedProfile = await res.json();
      Object.assign(user, updatedProfile);
    },

    async changeEmail(newEmail: string) {
      const res = await portalFetch('/user/email/change', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: newEmail })
      });

      if (!res.ok) {
        const errorText = await res.text();
        processes.error({
          title: 'Could not change email',
          message: errorText || 'Failed to change email'
        });
        throw new Error('Email change failed');
      }

      // Refresh profile to show pending email
      await this.refresh();
    },

    async changePassword(currentPassword: string, newPassword: string) {
      const res = await portalFetch('/user/password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to change password');
      }
    },
    
    async updateSettings(settings: Record<string, unknown>) {
      
      const res = await portalFetch('/user/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      })

      if (!res.ok) {
        processes.error({
          title: 'Could not update settings',
          message: `Failed to update settings. Server responded (${res.status}): ${await res.text()}`
        })
      }

      for (const key in settings) {
        user.settings[key] = settings[key];
      }
    },

    async getPersonalAccessTokens() {
      const res = await portalFetch('/user/getPats', {
        method: 'GET'
      });

      if (res.status === 200) {
        return (await res.json()) as PersonalAccessToken[];
      } else if (res.status === 401) {
        throw new Error('Unauthorized');
      }
      return [];
    },

    async createPersonalAccessToken(
      description: string,
      expiresAt: string,
      scope: string
    ) {
      const proc = processes.createProcess({ description: 'Creating PAT' });

      const res = await portalFetch('/user/createPat', {
        method: 'POST',
        body: JSON.stringify({
          description,
          expiresAt,
          scope
        })
      });

      if (res.ok) {
        proc.complete();
        return res.json();
      } else {
        proc.fail({ message: 'Saving to create PAT. Server responded: ' + res.status });
      }
    },

    async revokePersonalAccessToken(keyId: string) {
      const proc = processes.createProcess({ description: 'Revoking PAT' });

      const res = await portalFetch('/user/revokePat', {
        method: 'POST',
        body: JSON.stringify(keyId)
      });

      if (res.ok) {
        proc.complete();
        return (await res.json()) as PersonalAccessToken;
      } else {
        proc.fail({ message: 'Failed to create PAT. Server responded: ' + res.status });
      }
    },

    authorize,
    authorizeAll,
    
    authorizeSettingsPhrases(permission: 'read' | 'write') {
      return authorize('portal/settings/phrases:' + permission);
    }

  };
  
  function authorize(pattern: string) {
    const parts = pattern.toLowerCase().split(':').filter(r => r.length > 0);

    let resource: string[];
    let permission: string[] = [];
    if (parts.length > 0) {
      resource = parts[0].split('/')
      if (parts.length > 1) {
        permission = parts.slice(1);
      }
    } else {
      throw new Error(`Invalid AccessControlListQuery: ${pattern}`);
    }

    const aclQuery: AccessControlListQuery = {
      resource: resource,
      permission: permission
    };
    return acl.contains(aclQuery);
  }

  /**
   * Ensures the current user has access to all provided patterns
   * @param patterns
   */
  function authorizeAll(patterns?: string[]) {
    if (patterns === undefined) {
      return true;
    }
    
    if (!Array.isArray(patterns)) {
      throw new Error(`Invalid AccessControlListQuery: ${patterns}`);
    }
    for (let p of patterns) {
      if (!authorize(p)) {
        return false;
      }
    }
    return true;
  }
}
